import { memo, useMemo, type FunctionComponent, type ReactNode } from 'react';
import map from 'lodash/map';
import size from 'lodash/size';
import isSafeInteger from 'lodash/isSafeInteger';
import toSafeInteger from 'lodash/toSafeInteger';
import { FormattedMessage, FormattedDate } from 'react-intl';
// Material UI imports
import Box from '@mui/material/Box';
import TableSortLabel from '@mui/material/TableSortLabel';
// Material Icon imports
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
// Skillmore UI Components
import { shortDateOptions } from '@empathco/ui-components/src/common/intl';
import { getJsDateFromISO } from '@empathco/ui-components/src/helpers/datetime';
import { injectParams } from '@empathco/ui-components/src/helpers/path';
import SortArrow from '@empathco/ui-components/src/icons/SortArrow';
import CardSection from '@empathco/ui-components/src/elements/CardSection';
import TargetIcon from '@empathco/ui-components/src/elements/TargetIcon';
import SkillNewTag from '@empathco/ui-components/src/elements/SkillNewTag';
import TagLabel from '@empathco/ui-components/src/elements/TagLabel';
import StandardLink from '@empathco/ui-components/src/elements/StandardLink';
import MatchIndicator from '@empathco/ui-components/src/elements/MatchIndicator';
import DataTable from '@empathco/ui-components/src/elements/DataTable';
import ReloadingOverlay from '@empathco/ui-components/src/elements/ReloadingOverlay';
// local imports
import { MyOpportunity, MyOpportunitiesSortOrder, MyOpportunityStatus, BookingStatus } from '../graphql/types';
import { MY_OPPORTUNITIES_ORDER, DEFAULT_OPPORTUNITIES_DIRECTION } from '../graphql/customTypes';
import { PATH_MY_OPPORTUNITY } from '../config/paths';
import useModels from '../helpers/models';
import EmployeeName from '../elements/EmployeeName';
import { hasPagination, PaginationControlsComponent } from './PaginationControls';

type OpportunitiesTableProps = {
  readonly opportunities?: MyOpportunity[];
  readonly sortOrder?: MyOpportunitiesSortOrder | null;
  readonly direction?: boolean | null;
  readonly onSort: (sort?: MyOpportunitiesSortOrder, asc?: boolean | null) => void;
  readonly pending?: boolean | null;
  readonly savingId?: number;
  readonly failed?: boolean | null;
  readonly disabled?: boolean | null;
  readonly notFoundMessage: string;
  readonly filters?: ReactNode;
  readonly pagination?: ReactNode;
  readonly onSave?: (opp: MyOpportunity) => void;
};

const OpportunitiesTable: FunctionComponent<OpportunitiesTableProps> = ({
  opportunities,
  sortOrder,
  direction,
  onSort,
  pending = false,
  savingId,
  failed = false,
  disabled = false,
  notFoundMessage,
  filters,
  pagination,
  onSave
}) => {
  const { getLocationStr } = useModels();
  const withPagination = hasPagination(pagination as PaginationControlsComponent);
  const loading = pending && !opportunities;
  const reloading = Boolean(pending && opportunities);

  const [
    sortByDefault, sortByMatchRate, sortByGrowthRate, sortByTitle, sortByOwnerName, sortByLocation, sortByPublishedAt,
    sortByDuration, sortByStartDate
  ] = useMemo(() => map(MY_OPPORTUNITIES_ORDER, (sorting) =>
    () => onSort(sorting, sortOrder === sorting ? !direction : DEFAULT_OPPORTUNITIES_DIRECTION[sorting])
  ), [sortOrder, direction, onSort]);

  const actionsDisabled = disabled || pending || !opportunities || size(opportunities) < 1;

  const titles = useMemo(() => map([
    { id: MyOpportunitiesSortOrder.default, handler: sortByDefault },
    { id: MyOpportunitiesSortOrder.title, handler: sortByTitle },
    { id: MyOpportunitiesSortOrder.owner_name, handler: sortByOwnerName },
    { id: MyOpportunitiesSortOrder.location, handler: sortByLocation },
    { id: MyOpportunitiesSortOrder.published_at, handler: sortByPublishedAt },
    { id: MyOpportunitiesSortOrder.duration, handler: sortByDuration },
    { id: MyOpportunitiesSortOrder.start_date, handler: sortByStartDate },
    { id: MyOpportunitiesSortOrder.match_rate, handler: sortByMatchRate },
    { id: MyOpportunitiesSortOrder.growth_rate, handler: sortByGrowthRate }
  ], ({ id, handler }) => {
    const label = (
      <FormattedMessage
          key={id}
          id="sort.opportunities.value"
          values={{ sort: id === MyOpportunitiesSortOrder.default ? 'status' : id }}
      />
    );
    return id && handler ? (
      <TableSortLabel
          key={id}
          direction={(sortOrder === id ? direction : DEFAULT_OPPORTUNITIES_DIRECTION[id]) ? 'asc' : 'desc'}
          active={sortOrder === id}
          onClick={handler}
          disabled={actionsDisabled || (id === MyOpportunitiesSortOrder.default && sortOrder === id)}
          IconComponent={SortArrow}
      >
        {label}
      </TableSortLabel>
    ) : label;
  }), [
    actionsDisabled, sortOrder, direction,
    sortByDefault, sortByMatchRate, sortByGrowthRate, sortByTitle, sortByOwnerName, sortByLocation, sortByPublishedAt,
    sortByDuration, sortByStartDate
  ]);

  const rows = useMemo(() => map(opportunities, (myOpp) => {
    const { id, is_new, employee_status, match_rate, growth_rate, opportunity, booking } = myOpp || {};
    const {
      title, published_at, duration_value, duration_unit, sidegig, start_date, onsite, timezone_minutes, location, owner
    } = opportunity || {};
    const { status: bookingStatus, employee_requested_at } = booking || {};
    const is_applied = Boolean(bookingStatus === BookingStatus.employee_requested ||
      (employee_requested_at && bookingStatus && bookingStatus !== BookingStatus.employee_rejected));
    const is_saved = employee_status === MyOpportunityStatus.shortlist;
    return {
      selected: false,
      values: [
        /* eslint-disable react/jsx-props-no-spreading, react/jsx-key */
        <Box display="flex" flexDirection="column" alignItems="center">
          <TargetIcon
              Icon={BookmarkBorderIcon}
              ActiveIcon={BookmarkIcon}
              label="opportunities.status.not_saved"
              activeLabel="opportunities.status.saved"
              active={is_saved}
              // eslint-disable-next-line react/jsx-no-bind
              onClick={onSave ? () => onSave(myOpp) : undefined}
              disabled={disabled}
              pending={savingId && savingId === id ? true : undefined}
          />
          {is_applied ? (
            <TagLabel small variant="outlined" title="opportunities.applied"/>
          ) : undefined}
        </Box>,

        <>
          {is_new ? <SkillNewTag active/> : undefined}
          <StandardLink
              text={title}
              to={disabled ? undefined : injectParams(PATH_MY_OPPORTUNITY, { opp_id: id })}
          />
        </>,

        owner ? (
          <EmployeeName
              variant="inherit"
              employee={owner}
              manager
              disabled={disabled ? true : undefined}
          />
        ) : '—',

        <>
          {getLocationStr(location) || <FormattedMessage id="opportunities.not_specified"/>}
          <FormattedMessage
              id="opportunities.location_options"
              values={{ onsite, timezone: isSafeInteger(timezone_minutes) ? timezone_minutes : null, style: 'brackets' }}
          />
        </>,

        published_at ? <FormattedDate value={getJsDateFromISO(published_at)} {...shortDateOptions}/>
          : <FormattedMessage id="opportunities.not_specified"/>,

        <FormattedMessage
            id="opportunities.duration"
            values={{ duration: duration_value, unit: duration_unit, sidegig, withTitle: false }}
        />,

        start_date ? <FormattedDate value={getJsDateFromISO(start_date)} {...shortDateOptions}/>
          : <FormattedMessage id="opportunities.not_specified"/>,

        <Box display="flex" justifyContent="center" py={0.75}>
          <MatchIndicator value={toSafeInteger(match_rate)}/>
        </Box>,
        <Box display="flex" justifyContent="center" py={0.75}>
          <MatchIndicator value={toSafeInteger(growth_rate)} variant="planned" label="opportunities.growth_rate_value"/>
        </Box>

        /* eslint-enable react/jsx-props-no-spreading, react/jsx-key */
      ]
    };
  }), [disabled, savingId, opportunities, onSave, getLocationStr]);

  return (
    <>
      {filters ? (
        <CardSection narrow top flex>
          {filters}
        </CardSection>
      ) : undefined}
      <ReloadingOverlay reloading={reloading}>
        <CardSection narrow>
          <DataTable
              tableSize="small"
              titles={titles}
              empty={notFoundMessage}
              data={rows}
              pending={loading}
              failed={failed}
              lastLeftAlignedTitle={6}
          />
        </CardSection>
        {withPagination ? (
          <CardSection narrow flex bottom>
            {pagination}
          </CardSection>
        ) : pagination}
      </ReloadingOverlay>
    </>
  );
};

export default memo(OpportunitiesTable);
